/* eslint-disable react-refresh/only-export-components */

import React, { useContext, useMemo } from "react";
import type { ReactNode } from "react";
import { useU2000GridStateHandlers } from "../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import { useU2000SearchStateHandlers } from "../u2000Core/reducer/formReducer/U2000SearchStateHandlers";
import type { U2000GridStateHandlersReturn } from "../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import type { U2000SearchStateHandlersReturn } from "../u2000Core/reducer/formReducer/U2000SearchStateHandlers";
import { useU2000LUStateHandlers } from "../u2000Core/reducer/formReducer/U2000LUHandlers";
import type { U2000LUState } from "../u2000Core/reducer/formReducer/U2000LUState";
import type { U3110InitLU, U3110TaxRateSearchRow } from "./U3110Service";

export type InitLU = U2000LUState["personalization"] &
  U3110InitLU & {
    launchSearch: boolean;
    hideSearch: boolean;
    nbRecordPerPage: number;
  };

export interface U3110ContextType {
  luStateHandlers: ReturnType<typeof useU2000LUStateHandlers>;
  searchStateHandlers: U2000SearchStateHandlersReturn;
  gridStateHandlers: U2000GridStateHandlersReturn<U3110TaxRateSearchRow>;
  initLU: U3110InitLU;
}

interface U3110ContextProviderProps {
  initLU: U3110InitLU;
  children: ReactNode;
}

const U3110Context = React.createContext<U3110ContextType | undefined>(undefined);

export const U3110ContextProvider: React.FC<U3110ContextProviderProps> = ({ initLU, children }) => {
  const U2000LUStateHandlers = useU2000LUStateHandlers({ initLU: initLU as InitLU & U2000LUState });
  const U2000SearchStateHandlers = useU2000SearchStateHandlers({
    luName: "u3110",
    launchSearch: initLU.launchSearch,
    hideSearch: initLU.hideSearch,
  });
  const U2000GridStateHandlers = useU2000GridStateHandlers<U3110TaxRateSearchRow>({
    nbRecordPerPage: initLU.nbRecordPerPage ?? 25,
  });

  const contextValue = useMemo<U3110ContextType>(
    () => ({
      luStateHandlers: U2000LUStateHandlers,
      searchStateHandlers: U2000SearchStateHandlers,
      gridStateHandlers: U2000GridStateHandlers,
      initLU,
    }),
    [U2000GridStateHandlers, U2000SearchStateHandlers, U2000LUStateHandlers, initLU],
  );

  return <U3110Context.Provider value={contextValue}>{children}</U3110Context.Provider>;
};

export function useU3110Context(): U3110ContextType {
  const context = useContext(U3110Context);
  if (!context) {
    throw new Error("useU3110Context must be used within a U3110ContextProvider");
  }
  return context;
}
